import { validate as validateUuid } from 'uuid';

import { getAttendeeLocalLanguage } from "../components/live-event/utils";
import { HvHostMap, parseHvPath } from "../connection/helpers";
import { BrandliveEvent } from "../types/working-model";
import { setStorageItem } from "./local-storage";
import { Redirects } from "./redirects";
import { getSessionStorageItem, setSessionStorageItem } from "./session-storage";
import { getSSOSignOnUrl, isUsingCustomSSO } from "./sso-utils";
import { getRandomValue } from "./utils";

/**
 * One attempt per tab. If the provider round trip has already happened and the visitor is still
 * unregistered (denied by an SSO attribute rule, or they abandoned the flow at the provider) the
 * auth code has already been consumed and stripped from the URL, so an unguarded load-time redirect
 * would bounce them off the still-authenticated provider session on every reload
 */
export const getAttemptedAutoSSOLoginKey = (eventUuid: string): string => `auto_sso_login.${eventUuid}`;

export const isAutomaticSsoLoginEnabled = (eventBundle: BrandliveEvent | null): boolean => (
	!!eventBundle
	&& isUsingCustomSSO(eventBundle)
	&& eventBundle.registration_settings?.singleSignOn?.automaticSSOLogin === true
);

export interface AutomaticSsoLoginQueryParams {
	/** ?code from the provider - read it without consuming it, the registration step still needs it */
	ssoCode?: string | null;
	reRegisterCode?: string | null;
	magicLinkCode?: string | null;
	previouslyRegisteredEmail?: string | null;
	isModerator?: boolean;
}

export function shouldAutomaticallyLogInWithSso(
	eventBundle: BrandliveEvent | null,
	params: AutomaticSsoLoginQueryParams
): boolean {
	if (!eventBundle?.uuid || !eventBundle.registration_on) return false;
	if (!isAutomaticSsoLoginEnabled(eventBundle)) return false;

	// registration being closed disables the manual sign-in button, so there is nothing to send
	// the visitor out to
	if (eventBundle.registration_settings?.isClosed) return false;

	// each of these means the visitor either arrived holding their own identity, or is coming back
	// from the provider with an auth code that the registration step still has to exchange
	if (params.ssoCode || params.reRegisterCode || params.magicLinkCode) return false;
	if (params.previouslyRegisteredEmail || params.isModerator) return false;

	if (getSessionStorageItem(getAttemptedAutoSSOLoginKey(eventBundle.uuid))) return false;

	return true;
}

export function getAutomaticSsoLoginUrl(eventBundle: BrandliveEvent): string | null {
	const selectedSignOn = eventBundle.registration_settings?.singleSignOn?.singleSignOns?.[0];
	if (!selectedSignOn) return null;

	const ssoConfig = eventBundle.channel_settings?.sso?.[selectedSignOn];
	if (ssoConfig) return getSSOSignOnUrl(ssoConfig);

	const publicOauthIntegration = eventBundle.channel_settings?.oauth2Integration;
	if (!validateUuid(selectedSignOn) && publicOauthIntegration) {
		const state = getRandomValue();
		setStorageItem(`sso_state`, state, 1);

		const params = new URLSearchParams({ redirect_uri: `${location.origin}/sso-redirect`, state });
		return parseHvPath(HvHostMap.registration, `/e3-oauth-sign-in/${eventBundle.channel}?${params}`);
	}

	return null;
}

/**
 * Redirects to the SSO provider when the project has automatic login on and
 * nothing about this visit blocks it
 */
export function startAutomaticSsoLogin(
	eventBundle: BrandliveEvent | null,
	params: AutomaticSsoLoginQueryParams
): boolean {
	if (!shouldAutomaticallyLogInWithSso(eventBundle, params) || !eventBundle) return false;

	const signOnUrl = getAutomaticSsoLoginUrl(eventBundle);
	if (!signOnUrl) return false;

	setSessionStorageItem(getAttemptedAutoSSOLoginKey(eventBundle.uuid), 'true');
	const ssoReturnUrl = `${window.location.origin}${Redirects.toRegistration(getAttendeeLocalLanguage(eventBundle))}`;
	setStorageItem(`sso_event_url`, ssoReturnUrl, 1);
	window.location.href = signOnUrl;

	return true;
}
